/**
 * Service-level K8s networks, resources, and configuration (versions / runtimes
 * / CNIs / flavors / GPU models / resource groups / networks / addons).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApi, unwrap, k8sPath, arr, k8sKeys } from './_internal';

// ---- Networks / Resources / Config (service-level) -----------------------

export interface K8sNetwork {
  id: string;
  name?: string;
  description?: string;
  type?: string;
  /** CIDR — API field is `prefix` (like OpenNebula networks). */
  prefix?: string;
  cidr?: string;
}
export interface K8sNetworkSummary {
  name?: string;
  size?: string | number;
  leased?: string | number;
  attached?: string[];
}
/** CIDR of a k8s network tolerating either field. */
export function k8sNetworkCidr(n: K8sNetwork): string | undefined {
  return n.prefix || n.cidr;
}
/**
 * Real shape from /resources: a single resource-group descriptor object
 * `{id, zone, service:{service_uuid,service_name}}` (NOT an array of k8s workloads).
 * Display name = service.service_name.
 */
export interface K8sResource {
  id: string;
  zone?: string;
  service?: { service_uuid?: string; service_name?: string };
}

export function useK8sNetworks(serviceId: string | undefined) {
  return useQuery({
    queryKey: k8sKeys.networks(serviceId ?? ''),
    enabled: !!serviceId,
    retry: false,
    queryFn: async (): Promise<K8sNetwork[]> => {
      // Legacy List UI reads networks from /configurations.networks (not /networks directly).
      // Fall back to /networks if configurations doesn't include them.
      const body = unwrap<Record<string, unknown>>(await serviceApi.get(k8sPath(serviceId!, 'configurations')));
      const fromConfig = Array.isArray(body?.['networks']) ? (body!['networks'] as unknown[]) : null;
      if (fromConfig && fromConfig.length > 0) {
        return fromConfig.map((n) => {
          const o = (n ?? {}) as Record<string, unknown>;
          return { id: String(o.id ?? o.value ?? ''), name: (o.name as string | undefined) ?? (o.label as string | undefined), type: o.type as string | undefined, prefix: o.prefix as string | undefined, cidr: o.cidr as string | undefined };
        });
      }
      // Fallback: dedicated /networks endpoint.
      const nb = unwrap<Record<string, unknown>>(await serviceApi.get(k8sPath(serviceId!, 'networks')));
      return arr<K8sNetwork>(nb, 'networks');
    },
  });
}

export function useK8sNetworkSummary(serviceId: string | undefined, networkId: string | undefined) {
  return useQuery({
    queryKey: [...k8sKeys.networks(serviceId ?? ''), networkId ?? '', 'summary'] as const,
    enabled: !!serviceId && !!networkId,
    retry: false,
    queryFn: async () => {
      const body = unwrap<{ network?: K8sNetworkSummary } | K8sNetworkSummary>(await serviceApi.get(k8sPath(serviceId!, `networks/${networkId}/summary`)));
      return (body && typeof body === 'object' && 'network' in body ? body.network : body) as K8sNetworkSummary;
    },
  });
}

export function useCreateK8sNetwork(serviceId: string, options?: UseMutationOptions<unknown, unknown, Record<string, unknown>>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: Record<string, unknown>) => (await serviceApi.post(k8sPath(serviceId, 'networks'), p)).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: k8sKeys.networks(serviceId) }); options?.onSuccess?.(...a); },
  });
}

export function useK8sResources(serviceId: string | undefined) {
  return useQuery({
    queryKey: k8sKeys.resources(serviceId ?? ''),
    enabled: !!serviceId,
    retry: false,
    queryFn: async (): Promise<K8sResource[]> => {
      const body = unwrap<Record<string, unknown>>(await serviceApi.get(k8sPath(serviceId!, 'resources')));
      // Backend returns a single resource-group object {id,zone,service}, not an array.
      // Normalise to array for consistent consumer interface.
      const raw = body?.['resources'] ?? body;
      if (Array.isArray(raw)) return raw as K8sResource[];
      if (raw && typeof raw === 'object' && 'id' in (raw as object)) return [raw as K8sResource];
      return [];
    },
  });
}

export interface K8sFlavor { id?: string; name: string; type?: string; cores?: number; memory?: number; available?: number; disabled?: boolean }
export interface K8sGpuModel { id: string; label?: string; vram?: number; device_id?: string }
export interface K8sOption { label: string; value: string; disabled?: boolean }
export interface K8sResourceGroup { id?: string; name?: string; label?: string; value?: string }

export interface K8sConfig {
  versions: string[];
  runtimes: { id: string; name: string }[];
  cnis: K8sOption[];
  flavors: K8sFlavor[];
  gpuModels: K8sGpuModel[];
  resourceGroups: K8sResourceGroup[];
  /** Networks available for the cluster's `network.subnet` selection. */
  networks: K8sNetwork[];
  addons: { name: string; label?: string; description?: string }[];
  /** Server-suggested random slug used to seed the cluster uid (k8s-c-<randomly>),
   *  matching @console. Falls back to a client slug when absent. */
  randomly?: string;
}

/** Coerce {label,value}|string|{name} → a plain string. */
function asStr(x: unknown): string {
  if (typeof x === 'string') return x;
  if (x && typeof x === 'object') {
    const o = x as Record<string, unknown>;
    return String(o.value ?? o.name ?? o.label ?? '');
  }
  return String(x ?? '');
}

export function useK8sConfig(serviceId: string | undefined) {
  return useQuery({
    queryKey: k8sKeys.config(serviceId ?? ''),
    enabled: !!serviceId,
    retry: false,
    staleTime: 60_000,
    queryFn: async (): Promise<K8sConfig> => {
      const body = unwrap<Record<string, unknown>>(await serviceApi.get(k8sPath(serviceId!, 'configurations')));
      const arr = (k: string) => (Array.isArray(body[k]) ? (body[k] as unknown[]) : []);
      const versions = arr('versions').map(asStr).filter(Boolean);
      const runtimes = arr('runtimes').map((r) => {
        const o = (r ?? {}) as Record<string, unknown>;
        return { id: asStr(o.id ?? o.value ?? r), name: String(o.name ?? o.label ?? o.id ?? r) };
      });
      const cnis = arr('cnis').map((c) => {
        const o = (c ?? {}) as Record<string, unknown>;
        return { value: asStr(o.value ?? o.id ?? c), label: String(o.label ?? o.name ?? o.value ?? c), disabled: !!o.disabled };
      });
      const flavors: K8sFlavor[] = arr('flavors').map((f) => {
        const o = (f ?? {}) as Record<string, unknown>;
        return { id: o.id as string | undefined, name: asStr(o.name ?? o.value ?? f), type: o.type as string | undefined, cores: o.cores as number | undefined, memory: o.memory as number | undefined, available: o.available as number | undefined, disabled: !!o.disabled };
      });
      const gpuModels: K8sGpuModel[] = arr('gpu_models').map((g) => {
        const o = (g ?? {}) as Record<string, unknown>;
        return { id: asStr(o.id ?? o.value ?? g), label: (o.label as string | undefined) ?? (o.name as string | undefined), vram: o.vram as number | undefined, device_id: o.device_id as string | undefined };
      });
      const resourceGroups: K8sResourceGroup[] = arr('resource_groups').map((r) => {
        const o = (r ?? {}) as Record<string, unknown>;
        return { id: o.id as string | undefined, name: (o.name as string | undefined) ?? (o.label as string | undefined), label: o.label as string | undefined, value: o.value as string | undefined };
      });
      const networks: K8sNetwork[] = arr('networks').map((n) => {
        const o = (n ?? {}) as Record<string, unknown>;
        return { id: asStr(o.id ?? o.value ?? n), name: (o.name as string | undefined) ?? (o.label as string | undefined), type: o.type as string | undefined, prefix: o.prefix as string | undefined, cidr: o.cidr as string | undefined };
      });
      const addons = arr('addons').map((a) => {
        const o = (a ?? {}) as Record<string, unknown>;
        return { name: asStr(o.name ?? o.value ?? a), label: (o.label as string | undefined) ?? (o.title as string | undefined), description: o.description as string | undefined };
      });
      const randomly = asStr(body.randomly) || undefined;
      return { versions, runtimes, cnis, flavors, gpuModels, resourceGroups, networks, addons, randomly };
    },
  });
}
